import type { ExtractedFact } from 'services/imports/buildAmendmentPrompt';
import type { ImportPage } from 'services/imports/fileToPages';
import { buildMarkdown, parseMarkdown } from 'services/imports/markdownContract';
import type { ImportTarget, MarkdownLine } from 'services/imports/markdownContract';
import type { AmendmentSuggestion } from 'services/imports/reviewAmendments';
import { resolveSource } from 'services/imports/extractWithAssistant';
import type { PageLines } from 'services/imports/extractWithAssistant';

type SectionRange = { start: number; end: number };

const LINE_BREAK_RE = /\r\n|\n|\r/;
const HEADING_RE = /^#{1,2}\s+(.+?)\s*$/;
const TARGET_LINE_RE = /^\s*-\s+order\s*:\s*(.*?)\s*$/i;
const NAME_HEADING_RE = /^#\s+Imports\s*-\s*(.*?)\s*$/i;

const toOneLine = (text: string): string => text
  .replace(/\r\n|[\n\r\u2028\u2029]/g, ' ')
  .trim()
  .replace(/-->/g, '-- >');

const headingName = (line: string): string | null => {
  const match = HEADING_RE.exec(line);
  return match ? match[1].trim().toLowerCase() : null;
};

/** First section named `name`: heading index and the index of the next heading (or EOF). */
const findSection = (lines: readonly string[], name: string): SectionRange | null => {
  const start = lines.findIndex((line) => headingName(line) === name);
  if (start < 0) return null;
  const offset = lines.slice(start + 1).findIndex((line) => headingName(line) !== null);
  return { start, end: offset < 0 ? lines.length : start + 1 + offset };
};

const trimTrailingBlanks = (lines: readonly string[]): string[] => {
  const copy = [...lines];
  while (copy.length > 0 && copy[copy.length - 1].trim() === '') copy.pop();
  return copy;
};

/** Inserts lines at the end of the Data section, before trailing blanks; creates the section if missing. */
const appendToData = (text: string, newLines: readonly string[]): string => {
  const lines = text.split(LINE_BREAK_RE);
  const section = findSection(lines, 'data');
  if (!section) {
    return [...trimTrailingBlanks(lines), '', '## Data', ...newLines, ''].join('\n');
  }
  let at = section.end;
  while (at > section.start + 1 && lines[at - 1].trim() === '') at -= 1;
  return [...lines.slice(0, at), ...newLines, ...lines.slice(at)].join('\n');
};

const buildOrderLine = (target: ImportTarget | null): string => {
  if (target === null) return '- order:';
  switch (target.kind) {
    case 'new': return '- order: new';
    case 'existing': {
      const id = toOneLine(target.id);
      return id === '' ? '- order:' : `- order: ${id}`;
    }
    default: {
      const unreachable: never = target;
      return unreachable;
    }
  }
};

const rebuildSkeleton = (text: string, target: ImportTarget | null): string => {
  const lines = text.split(LINE_BREAK_RE);
  const nameMatch = lines.map((line) => NAME_HEADING_RE.exec(line)).find((match) => match !== null);
  const name = nameMatch ? nameMatch[1] : '';
  const kept = lines.filter((line) => line.trim() !== '' && headingName(line) === null && !NAME_HEADING_RE.test(line));
  const skeleton = buildMarkdown({ name, target, lines: [] });
  return kept.length === 0 ? skeleton : appendToData(skeleton, kept);
};

export const setTargetInMarkdown = (text: string, target: ImportTarget | null): string => {
  const lines = text.split(LINE_BREAK_RE);
  const orderLine = buildOrderLine(target);
  const section = findSection(lines, 'target');
  if (section) {
    const orderIndexes = lines
      .map((line, index) => (index > section.start && index < section.end && TARGET_LINE_RE.test(line) ? index : -1))
      .filter((index) => index >= 0);
    if (orderIndexes.length === 0) {
      return [...lines.slice(0, section.start + 1), orderLine, ...lines.slice(section.start + 1)].join('\n');
    }
    const [first] = orderIndexes;
    return lines
      .map((line, index) => (index === first ? orderLine : line))
      .filter((_, index) => index === first || !orderIndexes.includes(index))
      .join('\n');
  }
  const data = findSection(lines, 'data');
  if (data) {
    return [...lines.slice(0, data.start), '## Target', orderLine, '', ...lines.slice(data.start)].join('\n');
  }
  return rebuildSkeleton(text, target);
};

export const findAmendmentTarget = (lines: readonly MarkdownLine[], amendment: AmendmentSuggestion): MarkdownLine | null => {
  if (amendment.key === 'note') return null;
  const samePage = lines.find((line) => (
    line.key === amendment.key
    && line.status !== 'malformed'
    && line.src !== null
    && line.src.file === amendment.page.file
    && line.src.page === amendment.page.page
  ));
  return samePage ?? lines.find((line) => line.key === amendment.key && line.status !== 'malformed') ?? null;
};

const normalizeValue = (value: string): string => value
  .toLowerCase()
  .replace(/[$,]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\.$/, '');

export const extractedFactsByPage = (text: string, pages: readonly ImportPage[]): Map<string, ExtractedFact[]> => {
  const byPage = new Map<string, ExtractedFact[]>();
  parseMarkdown(text).lines.forEach((line) => {
    if (line.status === 'malformed' || line.src === null) return;
    const { src } = line;
    const page = pages.find((candidate) => candidate.fileName === src.file && candidate.pageNumber === src.page);
    if (!page) return;
    const facts = byPage.get(page.id) ?? [];
    facts.push({ key: line.key, value: line.value });
    byPage.set(page.id, facts);
  });
  return byPage;
};

/**
 * Flags data lines whose cited OCR line does not exist on their page. Such a line
 * stays in the markdown with its source, but is shown as `unverified`.
 */
export const markUnverified = (
  lines: readonly MarkdownLine[],
  pages: readonly ImportPage[],
  linesByPage: PageLines
): MarkdownLine[] => lines.map((line) => {
  if (line.status !== 'ok' || line.src === null) return line;
  return resolveSource(line.src, pages, linesByPage) === null ? { ...line, status: 'unverified' } : line;
});

export const toAmendmentRows = (
  amendments: readonly AmendmentSuggestion[],
  lines: readonly MarkdownLine[]
): (AmendmentSuggestion & { current: string | null })[] => amendments
  .map((amendment) => {
    const target = findAmendmentTarget(lines, amendment);
    return { ...amendment, current: target ? target.value : null };
  })
  .filter((row) => row.current === null || normalizeValue(row.current) !== normalizeValue(row.value));

export const toDisplayMessage = (message: string, maxLength = 200): string => {
  const jsonAt = message.indexOf(': {');
  const cut = (jsonAt < 0 ? message : message.slice(0, jsonAt)).trim();
  return cut.length > maxLength ? `${cut.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…` : cut;
};
